/**
 * PageHeader Component
 * 
 * Reusable page header with title, subtitle, actions
 * 
 * Version: 6.2.0
 */
import React, { ReactNode } from 'react'
import './PageHeader.css'
import Breadcrumb, { BreadcrumbItem } from './Breadcrumb'

interface ActionButton {
  label: string
  icon?: ReactNode
  onClick: () => void
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost'
  disabled?: boolean
  className?: string
}

interface PageHeaderProps {
  title: string
  subtitle?: string
  icon?: ReactNode
  breadcrumbs?: BreadcrumbItem[]
  actions?: ActionButton[]
  badge?: string
  badgeColor?: 'primary' | 'success' | 'warning' | 'danger' | 'info'
  variant?: 'default' | 'compact' | 'hero'
  className?: string
  children?: ReactNode
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  icon,
  breadcrumbs,
  actions,
  badge,
  badgeColor = 'primary',
  variant = 'default',
  className = '',
  children
}) => {
  const renderActions = () => {
    if (!actions || actions.length === 0) return null

    return (
      <div className="page-header-actions">
        {actions.map((action, index) => (
          <button
            key={index}
            className={`page-action-btn ${action.variant || 'secondary'} ${action.className || ''}`}
            onClick={action.onClick}
            disabled={action.disabled}
          >
            {action.icon && <span className="action-icon">{action.icon}</span>}
            <span className="action-label">{action.label}</span>
          </button>
        ))}
      </div>
    )
  }

  return (
    <header className={`page-header ${variant} ${className}`}>
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumb items={breadcrumbs} className="page-header-breadcrumb" />
      )}

      {/* Main Header */}
      <div className="page-header-main">
        <div className="page-header-content">
          {/* Icon */}
          {icon && (
            <div className="page-header-icon">
              {icon}
            </div>
          )}

          {/* Title Section */}
          <div className="page-header-text">
            <div className="page-header-title-row">
              <h1 className="page-header-title">{title}</h1>
              {badge && (
                <span className={`page-header-badge ${badgeColor}`}>
                  {badge}
                </span>
              )}
            </div>
            {subtitle && (
              <p className="page-header-subtitle">{subtitle}</p>
            )}
          </div>
        </div>

        {/* Actions */}
        {renderActions()}
      </div>

      {/* Custom Content */}
      {children && (
        <div className="page-header-custom">
          {children}
        </div>
      )}
    </header>
  )
}

interface SectionHeaderProps {
  title: string
  subtitle?: string
  action?: {
    label: string
    icon?: ReactNode
    onClick: () => void
  }
  className?: string
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  subtitle,
  action,
  className = ''
}) => {
  return (
    <div className={`section-header ${className}`}>
      <div className="section-header-text">
        <h2 className="section-header-title">{title}</h2>
        {subtitle && <p className="section-header-subtitle">{subtitle}</p>}
      </div>
      {action && (
        <button className="section-header-action" onClick={action.onClick}>
          {action.icon && <span className="action-icon">{action.icon}</span>}
          <span>{action.label}</span>
        </button>
      )}
    </div>
  )
}

export default PageHeader